import { pluralEn, type Count } from '../lib/format'

import type { Translations } from './types'

export const de: Translations = {
  common: {
    all: 'Max',
    back: 'Zurück',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    copy: 'Kopieren',
    copied: 'Kopiert',
    open: 'Öffnen',
    retry: 'Erneut versuchen',
    save: 'Speichern',
    close: 'Schließen',
    loading: 'Lädt',
    available: 'Verfügbar',
    total: 'Gesamt',
    amount: 'Betrag',
    network: 'Netzwerk',
    asset: 'Asset',
    status: 'Status',
    date: 'Datum',
    nothingYet: 'Noch nichts hier',
    somethingWentWrong: 'Etwas ist schiefgelaufen',
    reload: 'Neu laden',
    crashHint: 'Dieser Bildschirm konnte nicht angezeigt werden. Laden Sie die App neu — Ihr Guthaben und Ihre Anfragen bleiben unberührt.',
  },

  nav: {
    label: 'Hauptnavigation',
    home: 'Start',
    markets: 'Märkte',
    signals: 'Signale',
    trade: 'Handel',
    wallet: 'Wallet',
  },

  auth: {
    signIn: 'Anmelden',
    signInSubtitle: 'Melden Sie sich an, um zu Ihren Signalen und Ihrem Guthaben zurückzukehren',
    signInAction: 'Anmelden',
    signUp: 'Registrieren',
    signUpSubtitle: 'Eine E-Mail und ein Passwort genügen zum Start',
    signUpSubtitleInvite: 'Eine E-Mail, ein Passwort und ein Einladungscode genügen zum Start',
    email: 'E-Mail',
    emailPlaceholder: 'name@example.com',
    password: 'Passwort',
    passwordHint: 'Mindestens 8 Zeichen, Buchstaben und Ziffern',
    passwordConfirm: 'Passwort wiederholen',
    referralCode: 'Einladungscode',
    referralCodePlaceholder: 'Zum Beispiel 8K3M7QPL',
    referralCodeHint: 'Optional — nutzen Sie ihn, wenn ein 19CRT-Mitglied Sie eingeladen hat',
    referralCodeRequiredHint: 'Registrierung nur mit Einladung: Nutzen Sie den Link oder Code der Person, die Sie eingeladen hat',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} Bonus-${pluralEn(signals, 'Signal', 'Signale')}` : null,
        promo === null ? null : `einen Promo-Bonus von ${promo} USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `Wenn ein 19CRT-Mitglied Ihnen diesen Code gegeben hat, zählt die Einladung nach einer ersten Einzahlung von mindestens ${deposit} ${asset}`
        : `Wenn ein 19CRT-Mitglied Ihnen diesen Code gegeben hat, erhalten Sie ${perks.join(' und ')} nach einer ersten Einzahlung von mindestens ${deposit} ${asset}`
    },
    forgot: 'Passwort vergessen?',
    createAccount: 'Konto erstellen',
    haveAccount: 'Sie haben schon ein Konto?',
    noAccount: 'Noch kein Konto?',
    backToSignIn: 'Zurück zur Anmeldung',
    verifyTitle: 'E-Mail bestätigen',
    verifySent: (email: string) => `Wir haben einen Link an ${email} geschickt`,
    verifySentAnywhere: 'Wir haben einen Link an Ihre E-Mail-Adresse geschickt',
    verifyHint:
      'Öffnen Sie den Link aus der E-Mail, um die Adresse zu bestätigen: An sie gehen auch E-Mails zur Passwortwiederherstellung. Falls keine ankommt, prüfen Sie den Spam-Ordner.',
    verifyEmailPrompt: 'Geben Sie die E-Mail ein, mit der das Konto registriert ist',
    verifyResend: 'E-Mail erneut senden',
    verifyResendIn: (seconds: Count) => `Erneut senden in ${seconds.text}s möglich`,
    verifyResent: 'Eine weitere E-Mail ist unterwegs',
    verifyResentMaybe: 'Falls die Adresse registriert und noch nicht bestätigt ist, ist eine neue E-Mail unterwegs',
    verifyChecking: 'Link wird geprüft',
    verifyDone: 'E-Mail bestätigt',
    verifyDoneHint: 'App wird geöffnet',
    verifyDoneSignInHint: 'Anmeldung wird geöffnet',
    resetTitle: 'Passwort zurücksetzen',
    resetSubtitle: 'Geben Sie Ihre E-Mail ein und wir senden einen Link zum Ändern des Passworts',
    resetSend: 'Link senden',
    resetSent: 'Falls dieses Konto existiert, ist die E-Mail unterwegs',
    resetSentHint:
      'Wir verraten nie, ob eine Adresse registriert ist — sonst ließe sich dieses Formular nutzen, um fremde E-Mails auszuspähen. Prüfen Sie Ihren Posteingang und den Spam-Ordner.',
    newPassword: 'Neues Passwort',
    newPasswordSubtitle: 'Wählen Sie ein neues Passwort. Das alte verliert sofort seine Gültigkeit.',
    newPasswordSubmit: 'Passwort speichern',
    requestNewLink: 'Neuen Link anfordern',
    newPasswordDone: 'Passwort aktualisiert',
    newPasswordDoneHint: 'Anmeldung wird geöffnet',
    signOut: 'Abmelden',
    totpCode: 'Bestätigungscode',
    totpHint: 'Sechs Ziffern aus Ihrer Authenticator-App',
    recoveryCode: 'Wiederherstellungscode',
    recoveryCodeHint: 'Acht Zeichen aus den Codes, die Sie beim Einschalten der 2FA gespeichert haben',
    useRecoveryCode: 'Wiederherstellungscode verwenden',
    useAppCode: 'Authenticator-App verwenden',
    resetLinkInvalid: 'Dieser Link funktioniert nicht',
    errors: {
      invalidCredentials: 'Falsche E-Mail oder falsches Passwort',
      emailTaken: 'Diese E-Mail ist bereits registriert',
      weakPassword: 'Dieses Passwort ist zu kurz',
      passwordRule: 'Das Passwort muss Buchstaben und Ziffern enthalten',
      passwordsDiffer: 'Die Passwörter stimmen nicht überein',
      invalidEmail: 'Geben Sie eine Adresse im Format name@example.com ein',
      invalidReferralCode: 'Prüfen Sie den Einladungscode',
      referralRequired: 'Geben Sie Ihren Einladungscode ein',
      emailRequired: 'Geben Sie Ihre E-Mail ein',
      passwordRequired: 'Geben Sie Ihr Passwort ein',
      accountBlocked: 'Dieses Konto ist eingeschränkt. Bitte wenden Sie sich an den Support',
      rateLimited: 'Zu viele Versuche. Versuchen Sie es später erneut',
      totpRequired: 'Geben Sie den Code aus Ihrer Authenticator-App ein',
      totpInvalid: 'Der Code stimmt nicht. Prüfen Sie die Uhrzeit Ihres Telefons und versuchen Sie einen neuen',
      totpFormat: 'Ein App-Code hat sechs Ziffern, ein Wiederherstellungscode acht Zeichen',
      expiredToken: 'Dieser Link ist abgelaufen. Fordern Sie einen neuen an',
      verifyLinkUsed: 'Dieser Link wurde bereits verwendet. Melden Sie sich an — oder öffnen Sie die neueste E-Mail, falls Sie einen weiteren angefordert haben',
      missingToken: 'Dieser Link ist unvollständig. Öffnen Sie ihn vollständig aus der E-Mail',
      validation: 'Bitte prüfen Sie die ausgefüllten Felder',
      network: 'Keine Verbindung zum Server. Prüfen Sie Ihr Netzwerk',
    },
  },

  home: {
    promoLabel: 'Aktionen',
    banners: [
      {
        title: 'Gold-Signale',
        subtitle: 'Ein Code aus unserem Telegram-Kanal — ein Trade in einem Schritt',
        cta: 'Code eingeben',
      },
      {
        title: 'Märkte in Echtzeit',
        subtitle: 'Live-Kurse, Charts und Orderbücher für jedes Paar',
        cta: 'Märkte entdecken',
      },
      {
        title: 'Konto in Minuten aufladen',
        subtitle: 'USDT über TRC20 oder ERC20 einzahlen',
        cta: 'Einzahlen',
      },
    ],
    pauseAnnouncements: 'Mitteilungen pausieren',
    resumeAnnouncements: 'Mitteilungen fortsetzen',
    announcementsLabel: 'Mitteilungen',
    announcements: [
      'Prüfen Sie Asset und Netzwerk vor jeder Einzahlung',
      'Verwalten Sie den Kontoschutz in den Sicherheitseinstellungen',
      'Ihre Transaktionshistorie finden Sie unter Assets',
    ],
    totalBalance: 'Gesamtguthaben',
    quickTrade: 'Schnellhandel',
    quickTradeHint: 'Spot und Futures in einem Terminal',
    quickActions: 'Schnellaktionen',
    signalLive: 'Signal verfügbar',
    signalNext: 'Nächstes Signal',
    signalClosesIn: 'schließt in',
    signalOpensAt: (time: string) => `öffnet um ${time}`,
    signalsTitle: 'Gold-Signale',
    allSignals: 'Alle Signale',
    marketMoves: 'Marktbewegungen',
    pair: 'Paar',
    lastPrice: 'Kurs',
    change24h: '24-Std.-Änderung',
    /** After the figure in the home balance's change pill: « +1.30% 24h ». */
    change24hShort: '24 Std.',
    volume24h: '24-Std.-Volumen',
    allMarkets: 'Alle Märkte',
    pricesUnavailable: 'Kurse nicht verfügbar — das Portfolio kann nicht vollständig bewertet werden',
    ratesUnavailable: 'Kurse sind vorübergehend nicht verfügbar',
    marketsUnavailable: 'Die Marktliste ist vorübergehend nicht verfügbar',
    signalsNone: 'Gerade keine Signale — neue Codes erscheinen in unserem Telegram-Kanal',
  },

  markets: {
    title: 'Märkte',
    search: 'Paar suchen',
    spot: 'Spot',
    futures: 'Futures',
    gainers: 'Gewinner',
    losers: 'Verlierer',
    volume: '24-Std.-Volumen',
    volumeShort: 'Vol.',
    typeLabel: 'Markttyp',
    sortLabel: 'Sortierung',
    favourites: 'Favoriten',
    addFavourite: 'Zu Favoriten hinzufügen',
    removeFavourite: 'Aus Favoriten entfernen',
    emptyFavourites: 'Noch keine Favoriten — markieren Sie die Paare, die Sie verfolgen',
    clearSearch: 'Suche löschen',
    empty: 'Keine Treffer',
    emptyHint: 'Prüfen Sie das Kürzel des Paares — zum Beispiel BTC oder SOL.',
    emptySpot: 'Noch keine Spot-Paare eröffnet',
    emptyFutures: 'Noch keine Futures-Paare eröffnet',
    loadFailed: 'Die Marktliste konnte nicht geladen werden',
    pricesUnavailable: 'Kurse sind vorübergehend nicht verfügbar. Die Preise aktualisieren sich von selbst.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'Gold' } as Record<string, string>,
  },

  wallet: {
    title: 'Wallet',
    totalValue: 'Gesamtwert des Kontos',
    accounts: 'Meine Konten',
    latestWithdrawal: 'Letzte Auszahlung',
    coin: 'Coin',
    searchCoin: 'Coin suchen',
    quickActions: 'Schnellaktionen',
    coins: 'Coins',
    hideBalance: 'Guthaben ausblenden',
    showBalance: 'Guthaben anzeigen',
    loadError: 'Ihr Guthaben konnte nicht geladen werden',
    pricesUnavailable: 'Kurse nicht verfügbar — das Portfolio kann nicht vollständig bewertet werden',
    coinsEmpty: 'Noch keine Coins. Zahlen Sie ein und sie erscheinen hier.',
    accountSpot: 'Spot-Konto',
    accountSpotHint: 'Zum Handeln verfügbar',
    accountFutures: 'Futures-Konto',
    accountFuturesHint: 'Margin für Positionen',
    accountCopy: 'Copy-Trading (Archiv)',
    accountCopyHint: 'Auf Spot verschieben',
    actions: {
      deposit: 'Einzahlen',
      withdraw: 'Auszahlen',
      transfer: 'Übertragen',
      history: 'Historie',
    },
    deposit: {
      title: 'Einzahlen',
      history: 'Einzahlungshistorie',
      chain: 'Netzwerk',
      saveQr: 'QR-Code speichern',
      address: 'Einzahlungsadresse',
      copyAddress: 'Adresse kopieren',
      notice: 'Wichtig',
      noticeWrongNetwork: (asset: string, network: string) =>
        `Senden Sie nur ${asset} über ${network}. Über ein anderes Netzwerk gesendete Coins gehen verloren.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `Gutgeschrieben nach ${count.text} Netzwerk-${pluralEn(count, 'Bestätigung', 'Bestätigungen')}, meist in etwa ${minutes.text} ${pluralEn(minutes, 'Minute', 'Minuten')}.`,
      noticeMinimum: (amount: string, asset: string) =>
        `Die Mindesteinzahlung beträgt ${amount} ${asset}.`,
      waiting: 'Warten auf Ihre Überweisung',
      incoming: 'Einzahlungen unterwegs',
      incomingFirst: 'Im Netzwerk gesehen, Warten auf die erste Bestätigung',
      incomingProgress: (done: Count, total: Count) => `Bestätigungen: ${done.text} von ${total.text}`,
      selectAsset: 'Coin wählen',
      noAssets: 'Einzahlungen sind vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut.',
      noNetworks: 'Für diesen Coin ist noch kein Netzwerk verfügbar — wählen Sie ein anderes.',
      addressFailed: 'Es konnte keine Einzahlungsadresse abgerufen werden',
      noticePermanent: 'Die Adresse ist dauerhaft: Nutzen Sie sie für jede künftige Einzahlung.',
      qrSaved: 'QR-Code gespeichert',
      qrFailed: 'Der QR-Code konnte nicht gespeichert werden',
      paymentLink: 'Wallet-Link',
      paymentLinkHint: 'Öffnen Sie ihn in Ihrer Wallet — Netzwerk und Adresse sind bereits ausgefüllt.',
      copyLink: 'Link kopieren',
    },
    withdraw: {
      title: 'Auszahlen',
      history: 'Auszahlungshistorie',
      asset: 'Auszahlungs-Asset',
      selectAsset: 'Asset auswählen',
      network: 'Auszahlungsnetzwerk',
      address: 'Auszahlungsadresse',
      addressPlaceholder: (network: string) => `Geben Sie eine ${network}-Adresse ein`,
      addressPaste: 'Adresse einfügen',
      addressFromBook: (label: string): string => `In Ihrem Adressbuch bestätigt · ${label}`,
      paste: 'Einfügen',
      addressInvalid: (network: string) => `Das sieht nicht nach einer ${network}-Adresse aus`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `Das ist eine ${detected}-Adresse, ausgewählt ist aber ${selected}`,
      amount: 'Betrag',
      amountPlaceholder: '0.00',
      networkFee: 'Netzwerkgebühr',
      platformFee: (rate: string): string => `Plattformgebühr (${rate}%)`,
      feeFree: 'vorerst kostenlos',
      feeDynamic: 'zum aktuellen Gaspreis',
      willReceive: 'Sie erhalten',
      notice: 'Wichtig',
      noticeReview: 'Jede Anfrage wird vor dem Senden geprüft. Die Mittel werden sofort reserviert.',
      noticeTiming: 'Innerhalb von 24 Stunden nach der Freigabe gesendet.',
      noticeMinimum: (amount: string, asset: string) =>
        `Die Mindestauszahlung beträgt ${amount} ${asset}.`,
      noticeSupport:
        'Prüfen Sie die Adresse selbst: Eine Überweisung an die falsche lässt sich nicht rückgängig machen. Der Support klärt Streitfälle anhand der Anfragenummer.',
      insufficient: 'Nicht genug Guthaben',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `Heute noch möglich: ${remaining} von ${limit} USDT.` : `Nicht verifizierte Konten können bis zu ${limit} USDT pro Tag auszahlen; ${remaining} verbleiben. Verifizieren Sie sich, um das Limit zu erhöhen.`,
      addressNotConfirmed: 'Auszahlungen gehen nur an im Adressbuch bestätigte Adressen',
      limitReached: (verified: boolean): string => (verified ? 'Tägliches Auszahlungslimit erreicht' : 'Tageslimit für nicht verifizierte Konten erreicht — verifizieren Sie sich im Profil'),
      belowMinimum: (amount: string, asset: string) => `Mindestens ${amount} ${asset}`,
      confirmTitle: 'Anfrage prüfen',
      confirmSubtitle: 'Eine Auszahlung lässt sich nach dem Senden nicht mehr stornieren. Prüfen Sie Netzwerk und Adresse.',
      confirmAction: 'Auszahlung bestätigen',
      failed: 'Die Anfrage konnte nicht erstellt werden',
      submitted: 'Anfrage erstellt',
      submittedHint: 'Nach der Prüfung gehen die Mittel ins Netzwerk. Verfolgen Sie den Status in Ihren Auszahlungsanfragen.',
      requestId: 'Anfragenummer',
      statusPending: 'In Prüfung',
      toRequests: 'Zu den Auszahlungsanfragen',
      empty: 'Derzeit sind keine Assets zur Auszahlung verfügbar',
      loadFailed: 'Die Asset-Liste konnte nicht geladen werden',
      routeUnavailable: 'Auszahlungen über dieses Netzwerk sind gerade nicht möglich — wählen Sie ein anderes Netzwerk',
      invalidAmount: 'Der Betrag passt nicht: Prüfen Sie die Genauigkeit und die Netzwerkgebühr',
      edit: 'Anfrage bearbeiten',
    },
    withdrawals: {
      title: 'Auszahlungsanfragen',
      toAddress: (address: string): string => `an ${address}`,
      note: 'Abgelehnte und fehlgeschlagene Anfragen geben die Reserve auf Ihr Konto zurück. Öffnen Sie eine Anfrage, um ihre Transaktion oder den Grund zu sehen.',
      empty: 'Noch keine Auszahlungsanfragen. Jede Anfrage zeigt hier ihren Status, von der Prüfung bis zur Netzwerkbestätigung.',
      loadError: 'Ihre Anfragen konnten nicht geladen werden',
      details: 'Auszahlungsanfrage',
      created: 'Erstellt',
      transaction: 'Transaktion',
      openExplorer: 'Im Explorer öffnen',
      reason: 'Grund',
      status: {
        pending: 'In Prüfung',
        approved: 'Freigegeben',
        rejected: 'Abgelehnt',
        broadcasting: 'Wird gesendet',
        sent: 'Gesendet',
        confirmed: 'Bestätigt',
        failed: 'Fehlgeschlagen',
      },
      statusHint: {
        pending: 'Die Anfrage wird geprüft. Der Betrag ist auf Ihrem Konto reserviert.',
        approved: 'Geprüft und bereit zum Senden ins Netzwerk.',
        rejected: 'Die Anfrage wurde abgelehnt; die Reserve ist zurück auf Ihrem Konto.',
        broadcasting: 'Die Transaktion wird ins Netzwerk gesendet.',
        sent: 'Die Transaktion ist im Netzwerk und wartet auf Bestätigung.',
        confirmed: 'Das Netzwerk hat die Transaktion bestätigt. Die Mittel sind angekommen.',
        failed: 'Das Senden ist fehlgeschlagen; die Reserve ist zurück auf Ihrem Konto.',
      },
    },
    transfer: {
      title: 'Übertragen',
      from: 'Von',
      fromAvailable: (amount: string): string => `Von · ${amount} verfügbar`,
      to: 'Nach',
      submit: 'Übertragen',
      swap: 'Die beiden Konten tauschen',
      selectFrom: 'Quellkonto',
      selectTo: 'Zielkonto',
      selectAsset: 'Coin wählen',
      after: 'Nach der Übertragung',
      insufficient: 'Nicht genug Guthaben auf dem Quellkonto',
      done: 'Übertragung abgeschlossen',
      hint: 'Übertragungen zwischen Ihren eigenen Konten sind sofort und kostenlos.',
      empty: 'Noch nichts zu übertragen — zahlen Sie zuerst ein.',
      failed: 'Die Übertragung konnte nicht abgeschlossen werden. Versuchen Sie es erneut',
    },
    history: {
      title: 'Transaktionshistorie',
      deposit: 'Einzahlung',
      withdrawal: 'Auszahlung',
      trade: 'Trade',
      fee: 'Gebühr',
      transfer: 'Übertragung',
      pnl: 'Ergebnis',
      empty: 'Noch keine Transaktionen. Einzahlungen, Auszahlungen und Trades erscheinen hier.',
      emptyFiltered: 'Keine Transaktionen entsprechen diesem Filter',
      loadError: 'Ihre Historie konnte nicht geladen werden',
      today: 'Heute',
      yesterday: 'Gestern',
      balanceAfter: (amount: string) => `Guthaben ${amount}`,
      onlyAccount: (account: string) => `nur ${account}`,
      resetAccount: 'Alle Konten anzeigen',
      accountFilter: 'Konto',
      allAccounts: 'Alle Konten',
      withdrawalsLink: 'Status der Auszahlungsanfragen',
      badges: {
        credited: 'Gutgeschrieben',
        sent: 'Gesendet',
        fee: 'Gebühr',
        reserved: 'Reserviert',
        returned: 'Zurückgebucht',
        released: 'Freigegeben',
        filled: 'Ausgeführt',
        settled: 'Abgerechnet',
        liquidated: 'Liquidiert',
        done: 'Fertig',
        adjusted: 'Angepasst',
        paid: 'Ausgezahlt',
      },
      filters: {
        all: 'Alle',
        rewards: 'Prämien',
        deposits: 'Einzahlungen',
        withdrawals: 'Auszahlungen',
        trades: 'Trades',
        transfers: 'Übertragungen',
      },
      types: {
        deposit: 'Einzahlung',
        withdrawal: 'Auszahlung',
        withdrawal_fee: 'Auszahlungsgebühr',
        withdrawal_platform_fee: 'Plattform-Auszahlungsgebühr',
        withdrawal_lock: 'Auszahlung reserviert',
        withdrawal_release: 'Auszahlungsreserve zurückgebucht',
        order_lock: 'Für eine Limit-Order reserviert',
        order_release: 'Order-Reserve zurückgebucht',
        bonus: 'Promo-Bonus',
        trade_buy: 'Kauf',
        trade_sell: 'Verkauf',
        trading_fee: 'Handelsgebühr',
        realized_pnl: 'Trade-Ergebnis',
        signal_programmed_profit: 'Programmierter Signalgewinn',
        funding: 'Funding',
        margin_lock: 'Margin reserviert',
        margin_release: 'Margin freigegeben',
        liquidation: 'Liquidation',
        transfer_in: 'Übertragung ein',
        transfer_out: 'Übertragung aus',
        copy_allocation: 'Für Copy-Trading zugeteilt',
        copy_release: 'Aus Copy-Trading zurückgebucht',
        copy_commission: 'Copy-Trading-Gebühr',
        referral: 'Empfehlungsprämie',
        adjustment: 'Korrektur',
      },
    },
  },

  trade: {
    title: 'Handel',
    menu: {
      account: 'Konto',
      trading: 'Handel',
      more: 'Mehr',
    },
    long: 'Long',
    short: 'Short',
    buy: 'Kaufen',
    sell: 'Verkaufen',
    marketOrder: 'Market',
    limitOrder: 'Limit',
    price: 'Kurs',
    marketPrice: 'Zum Marktpreis',
    quantity: 'Menge',
    leverage: 'Hebel',
    takeProfitStopLoss: 'Take Profit / Stop Loss',
    positions: 'Positionen',
    orders: 'Orders',
    history: 'Historie',
    copyTrading: 'Copy-Trading',
    unrealizedPnl: 'Unrealisiertes P&L',
    unrealizedPnlShort: 'P&L',
    margin: 'Margin',
    entry: 'Einstieg',
    size: 'Größe',
    liquidation: 'Liquidation',
    close: 'Schließen',
    orderBook: 'Orderbuch',
    noPositions: 'Platzieren Sie oben eine Order und die Position erscheint hier.',
    balance: 'Guthaben',
    perpetual: 'Perpetual',
    perpShort: 'Perp',
    fundingIn: (time: string) => `in ${time}`,
    position: 'Position',
    closeAtMarket: 'Zum Marktpreis schließen',
    keepPosition: 'Position behalten',
    placeBuyOrder: 'Kauforder platzieren',
    placeSellOrder: 'Verkaufsorder platzieren',
    placeLongOrder: 'Long-Order platzieren',
    placeShortOrder: 'Short-Order platzieren',
    isolatedMargin: 'Isolierte Margin',
    crossMargin: 'Cross-Margin',
    markShort: 'Mark',
    liqPrice: 'Liq.-Kurs',
    noPositionsTitle: 'Keine offenen Positionen',
    noOrdersTitle: 'Keine aktiven Orders',
    noHistoryTitle: 'Noch keine Trades',
    pair: 'Paar',
    selectPair: 'Paar auswählen',
    sideLabel: 'Richtung',
    orderType: 'Ordertyp',
    sizeShare: 'Anteil am verfügbaren Guthaben',
    quantityShort: 'Menge',
    takeProfit: 'Take Profit',
    stopLoss: 'Stop Loss',
    tabsLabel: 'Meine Trades',
    openChart: 'Chart',
    openLong: (symbol: string) => `Long ${symbol}`,
    openShort: (symbol: string) => `Short ${symbol}`,
    buyAsset: (symbol: string) => `${symbol} kaufen`,
    sellAsset: (symbol: string) => `${symbol} verkaufen`,
    enterQuantity: 'Menge eingeben',
    insufficient: 'Nicht genug Guthaben auf diesem Konto',
    belowMinNotional: (amount: string, asset: string) =>
      `Die Mindestordergröße beträgt ${amount} ${asset}`,
    bookEmpty: 'Noch keine ruhenden Orders',
    bookUnavailable: 'Orderbuch nicht verfügbar',
    tradingUnavailable:
      'Der Handelsdienst antwortet nicht, daher können gerade keine Orders platziert werden. Guthaben, Einzahlungen und Auszahlungen sind nicht betroffen.',
    marketsFailed: 'Die Paarliste konnte nicht geladen werden',
    noMarkets: 'Noch keine Handelspaare — sie erscheinen, sobald Märkte live gehen.',
    confirmTitle: 'Order prüfen',
    confirmSubtitle: 'Eine ausgeführte Order lässt sich nicht rückgängig machen.',
    marginRequired: 'Verwendete Margin',
    cost: 'Gesamt',
    liquidationEstimate: 'Geschätzte Liquidation',
    submitted: 'Order platziert',
    submitFailed: 'Die Order konnte nicht platziert werden',
    futuresSoon: 'Futures sind vorübergehend nicht verfügbar. Der Spot-Handel funktioniert.',
    marginMode: 'Margin',
    cross: 'Cross',
    isolated: 'Isoliert',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'Cross: Das gesamte Futures-Guthaben deckt die Position'
        : 'Isoliert: Nur die Margin dieser Position ist im Risiko',
    reduceOnly: 'Nur reduzieren',
    futuresAccount: 'Futures-Konto',
    equity: 'Eigenkapital',
    marginUsed: 'In Margin',
    marginRatio: 'Risikoniveau',
    funding: 'Funding',
    fundingRate: '8-Std.-Rate',
    nextFunding: 'Nächste',
    fundingPaid: 'Funding',
    protection: 'Absicherung',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'Take Profit und Stop Loss',
    protectionSheetSubtitle: 'Die Engine schließt zum Marktpreis, sobald der Kurs eine Marke berührt.',
    clearLevel: 'Keine Marke',
    addMargin: 'Margin hinzufügen',
    removeMargin: 'Margin entnehmen',
    marginDelta: 'Margin-Betrag',
    marginDeltaHint: 'Isolierte Position: mehr Margin schiebt die Liquidation weiter weg',
    addMarginKyc: 'Das Hinzufügen von Margin ist nach Freigabe Ihrer Dokumente möglich. Margin entnehmen, Take Profit und Stop Loss ändern oder die Position schließen können Sie bereits.',
    saveProtection: 'Speichern',
    protectionSaved: 'Absicherung aktualisiert',
    protectionFailed: 'Die Position konnte nicht aktualisiert werden',
    protectionInvalid: 'Die Marke liegt auf der falschen Seite des Einstiegskurses',
    leverageTooHigh: 'Der Hebel übersteigt das Marktmaximum',
    leverageTier: (cap: number, notional: string) => `Bis zu ${cap}× für eine Position von ${notional} USDT`,
    futuresDisabled: 'Futures-Orders werden derzeit nicht angenommen',
    liquidated: 'Liquidiert',
    restingToast: (symbol: string, price: string) => `Limit-Order ${symbol} bei ${price} ruht und wartet auf den Kurs`,
    cancelledToast: 'Order storniert, Mittel freigegeben',
    costSell: 'Sie erhalten',
    costBasis: 'Kosten',
    enterProtection: 'Die Take-Profit- oder Stop-Loss-Marke ist kein gültiger Kurs',
    leverageLocked: (leverage: number) => `Der Hebel ist durch die offene Position auf ${leverage}× festgelegt. Schließen Sie sie, um ihn zu ändern`,
    marginReleased: 'Margin freigegeben',
    realizedEstimate: 'Geschätztes Ergebnis',
    addsPosition: (quantity: string, asset: string) => `Fügt der offenen Position ${quantity} ${asset} hinzu`,
    reducesPosition: (quantity: string, asset: string) => `Reduziert die offene Position um ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `Schließt die offene Position von ${quantity} ${asset}`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `Schließt die Position von ${closeQuantity} ${asset} und eröffnet ${openQuantity} ${asset} in die andere Richtung`,
    byAccount: 'ganzes Konto',
    cancelledToastPlain: 'Order storniert',
    fillBuy: 'Kauf',
    fillSell: 'Verkauf',
    marginFloor: (floor: string) => `Die Margin darf die Anfangsmargin von ${floor} USDT nicht unterschreiten`,
    marginRemovable: (amount: string) => `Bis zu ${amount} USDT können entnommen werden`,
    enterPrice: 'Limit-Kurs eingeben',
    markPrice: 'Mark-Kurs',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'Der Kurs ist veraltet — der Quote-Feed hängt hinterher. Versuchen Sie es in ein paar Sekunden erneut.',
    marketDisabled: 'Der Handel in diesem Paar ist pausiert',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `${side} ${quantity} ${asset} bei ${price} — ausgeführt`,
    closedToast: (symbol: string, pnl: string) => `${symbol} geschlossen, Ergebnis ${pnl} USDT`,
    positionsFailed: 'Positionen konnten nicht geladen werden',
    noOrders: 'Eine Limit-Order wartet hier, bis sie ausgeführt wird.',
    ordersFailed: 'Orders konnten nicht geladen werden',
    noHistory: 'Ihr erster ausgeführter Trade erscheint in dieser Liste.',
    historyFailed: 'Die Trade-Historie konnte nicht geladen werden',
    cancelOrder: 'Stornieren',
    cancelFailed: 'Die Order konnte nicht storniert werden',
    closeFailed: 'Die Position konnte nicht geschlossen werden',
    closeConfirmTitle: 'Position schließen?',
    closeConfirmSubtitle:
      'Die Position wird zum Marktpreis geschlossen und das Ergebnis wird endgültig. Das lässt sich nicht rückgängig machen.',
  },

  chart: {
    title: 'Chart',
    interval: 'Intervall',
    chartType: 'Chart-Typ',
    candles: 'Kerzen',
    line: 'Linie',
    high24h: '24-Std.-Hoch',
    low24h: '24-Std.-Tief',
    volume24h: '24-Std.-Volumen',
    empty: 'Der Chart erscheint, sobald dieses Paar Daten hat',
    failed: 'Der Chart konnte nicht geladen werden',
    unknownPair: 'Kein solches Paar — wählen Sie eines im Tab „Märkte“',
    toMarkets: 'Zu den Märkten',
  },

  security: {
    title: 'Sicherheit',
    subtitle: 'Ein zweiter Faktor schützt Anmeldung und Auszahlungen',
    statusOn: 'An',
    statusOff: 'Aus',
    explain:
      'Der Code aus Ihrer Authenticator-App wird bei der Anmeldung und bei jeder Auszahlungsanfrage verlangt. Ohne ihn ist das Konto nur durch das Passwort geschützt.',
    setup: 'Authenticator einrichten',
    step1: 'Scannen Sie den Code in Google Authenticator, 1Password oder Aegis',
    secretLabel: 'Oder geben Sie das Geheimnis manuell ein',
    step2: 'Geben Sie den Code aus der App ein',
    enable: 'Einschalten',
    enabled: 'Zwei-Faktor ist an',
    disableTitle: 'Zwei-Faktor ausschalten',
    disableHint: 'Geben Sie den aktuellen Code ein, um zu bestätigen, dass das Telefon Ihnen gehört',
    disable: 'Ausschalten',
    disabled: 'Zwei-Faktor ist aus',
    codeInvalid: 'Der Code stimmt nicht. Prüfen Sie die Uhrzeit Ihres Telefons und versuchen Sie einen neuen',
    failed: 'Die Sicherheitseinstellungen konnten nicht geändert werden',
    withdrawalCode: 'Bestätigungscode',
    withdrawalCodeHint: 'Auszahlungen sind durch Ihren zweiten Faktor geschützt: Geben Sie den Code aus der App ein',
    recoveryTitle: 'Wiederherstellungscodes',
    recoveryIntro: 'Bewahren Sie diese Codes sicher auf. Jeder öffnet einmal eine Anmeldung oder Auszahlung, wenn Ihr Telefon nicht erreichbar ist. Sie werden nicht erneut angezeigt.',
    recoveryCopy: 'Codes kopieren',
    recoveryCopied: 'Codes kopiert',
    recoverySaved: 'Ich habe die Codes gespeichert',
    recoveryLeft: (count: number): string => `Verbleibende Wiederherstellungscodes: ${count}`,
    recoveryRegenerate: 'Neue Wiederherstellungscodes',
    recoveryRegenerateHint: 'Die alten Codes verlieren ihre Gültigkeit. Geben Sie einen Code aus der App ein',
    recoveryRegenerated: 'Neue Wiederherstellungscodes ausgestellt',
    codeOrRecovery: 'Sechs Ziffern aus der App oder ein Wiederherstellungscode',
  },

  addressBook: {
    title: 'Auszahlungsadressen',
    hint: 'Eine neue Adresse wird aktiv, nachdem Sie den Link aus der E-Mail geöffnet haben, damit eine gestohlene Sitzung keine Mittel an ihre eigene Adresse senden kann.',
    add: 'Adresse hinzufügen',
    address: 'Adresse',
    label: 'Bezeichnung',
    labelPlaceholder: 'Zum Beispiel Ledger',
    send: 'Bestätigungs-E-Mail senden',
    saved: 'Gespeicherte Adressen',
    empty: 'Noch keine Adressen',
    emptyBody: 'Fügen Sie die Adresse hinzu, an die Sie auszahlen: Nach der Bestätigung per E-Mail ist sie im Auszahlungsformular nur einen Tipp entfernt.',
    statusConfirmed: 'bestätigt',
    statusPending: 'wartet auf E-Mail-Bestätigung',
    resend: 'Erneut senden',
    resendLink: 'Bestätigungs-E-Mail erneut senden',
    remove: 'Adresse entfernen',
    removeTitle: 'Diese Adresse löschen?',
    removeHint: 'Auszahlungen an sie werden gestoppt, bis Sie sie erneut hinzufügen und bestätigen.',
    linkSent: 'E-Mail gesendet — öffnen Sie den Link, um die Adresse zu bestätigen',
    alreadyConfirmed: 'Diese Adresse ist bereits bestätigt',
    invalid: 'Die Adresse passt nicht zum gewählten Netzwerk',
    confirmed: (short: string): string => `Adresse ${short} bestätigt`,
    confirmFailed: 'Der Link ist ungültig oder abgelaufen',
  },

  sessions: {
    title: 'Sitzungen und Geräte',
    subtitle: 'Wo Ihr Konto angemeldet ist',
    current: 'Dieses Gerät',
    started: 'Angemeldet',
    lastSeen: 'Aktiv',
    end: 'Beenden',
    endOthers: 'Alle anderen Sitzungen beenden',
    ended: 'Sitzung beendet',
    endedOthers: (count: number): string => `Beendete Sitzungen: ${count}`,
    empty: 'Keine weiteren aktiven Sitzungen',
    failed: 'Sitzungen konnten nicht geladen werden',
    hint: 'Eine beendete Sitzung verliert innerhalb von fünfzehn Minuten den Zugriff: So lange lebt ein ausgestellter Zugriffsschlüssel.',
  },

  profile: {
    title: 'Profil',
    accountId: 'Konto-ID',
    idCopied: 'ID kopiert',
    loadFailed: 'Ihr Profil konnte nicht geladen werden',
    partnerProgram: 'Partnerprogramm',
    partnerProgramHint: 'Team, Stufe und Prämien',

    verification: 'Verifizierung',
    verificationNone: 'Nicht begonnen',
    verificationPending: 'In Prüfung',
    verificationInReview: 'In Prüfung',
    verificationApproved: 'Verifiziert',
    verificationRejected: 'Abgelehnt',
    verificationResubmit: 'Neue Dokumente nötig',
    verificationCta: 'Starten',
    verificationCtaAgain: 'Erneut einreichen',
    verificationPrompt: 'Laden Sie Ihren Reisepass hoch — dauert zwei Minuten',
    verificationPendingHint: 'Ein Operator prüft Dokumente üblicherweise innerhalb eines Tages.',
    verificationRejectedHint: 'Der Operator hat die Dokumente nicht akzeptiert. Sie können erneut einreichen.',
    verificationOpen: 'Antrag öffnen',

    withdrawalAddressesHint: 'Adressbuch, per E-Mail bestätigt',
    withdrawalAddresses: 'Auszahlungsadressen',
    addressesSubtitle: 'Adressen, an die Sie bereits ausgezahlt haben',
    addressesEmpty: 'Sie haben noch nicht ausgezahlt. Die Adresse Ihrer ersten Anfrage erscheint hier.',
    addressesFailed: 'Die Adressen konnten nicht geladen werden',
    addressCopied: 'Adresse kopiert',

    security: 'Sicherheit',
    accountAccess: 'E-Mail und Passwort',
    sessions: 'Sitzungen und Geräte',
    sessionsHint: 'Wo Sie angemeldet sind',
    securityOn: 'Zwei-Faktor ist an',
    securityOff: 'Zwei-Faktor einrichten',
    securitySubtitle: 'Kontozugriff',
    securityEmailVerified: 'E-Mail bestätigt',
    securityEmailUnverified: 'E-Mail nicht bestätigt',
    securityPassword: 'Passwort ändern',
    securityPasswordHint: 'Wir senden Ihnen einen Link, um ein neues festzulegen',

    notifications: 'Benachrichtigungen',
    pushOn: 'Push an',
    pushOff: 'Push aus',
    pushToggle: 'Push-Benachrichtigungen',
    pushUnsupported: 'Dieser Browser kann keine Push-Benachrichtigungen',
    pushDenied: 'Benachrichtigungen sind in Ihren Browser-Einstellungen blockiert',
    pushUnavailable: 'Push-Benachrichtigungen sind auf dem Server noch nicht eingerichtet',

    pushFailed: 'Push-Benachrichtigungen konnten nicht aktiviert werden',

    theme: 'Design',
    themeHint: 'Schwarz ist die Marke und spart Akku auf OLED. Hell ist für helles Sonnenlicht. „System“ folgt Ihrem Telefon.',
    themeNames: { system: 'System', dark: 'Dunkel', light: 'Hell' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'Folgt der Geräteeinstellung',
    language: 'Sprache',
    languageHint: 'Oberflächensprache. Beträge behalten ein Format; Datumsangaben folgen der Sprache.',

    support: 'Support',
    supportTitle: 'Support kontaktieren',
    supportChoose: 'Wählen Sie, wie Sie uns erreichen',
    supportVia: 'Telegram oder E-Mail',
    supportTelegram: 'Über Telegram schreiben',
    supportEmail: 'E-Mail schreiben',
    about: 'Über die App',
    version: 'Version',

    signOut: 'Abmelden',
    signOutTitle: 'Von diesem Konto abmelden?',
    signOutBody:
      'Guthaben, offene Positionen und Ihre Signalhistorie bleiben genau so, wie sie sind — bei der nächsten Anmeldung sind sie wieder da. Andere Geräte bleiben angemeldet.',

    kyc: {
      title: 'Verifizierung',
      intro:
        'Nur der prüfende Operator sieht dies. Die Dokumentnummer wird verschlüsselt gespeichert und nie vollständig angezeigt.',
      loadFailed: 'Der Antrag konnte nicht geladen werden',

      document: 'Dokument',
      documentType: 'Dokumenttyp',
      typePassport: 'Reisepass',
      typeIdCard: 'Personalausweis',
      typeDriverLicense: 'Führerschein',
      number: 'Dokumentnummer',
      numberPlaceholder: '4510 123456',
      numberHint: 'Serie und Nummer, genau wie aufgedruckt',
      country: 'Ausstellungsland',
      countryPlaceholder: 'Land wählen',
      issueDate: 'Ausgestellt am',
      expiryDate: 'Gültig bis',

      personal: 'Dokumentinhaber',
      lastName: 'Nachname',
      firstName: 'Vorname',
      dateOfBirth: 'Geburtsdatum',

      documents: 'Dokumentfoto',
      docMain: 'Hauptseite',
      docMainHint: 'Die Doppelseite mit Foto und Nummer',
      docFront: 'Vorderseite',
      docFrontHint: 'Die Seite mit Foto und Nummer',
      choose: 'Datei wählen',
      replace: 'Ersetzen',
      remove: 'Entfernen',
      fileHint: (size: string) => `JPG, PNG oder PDF, bis zu ${size} MB`,
      megabytes: 'MB',

      submit: 'Zur Prüfung einreichen',
      submitFailed: 'Der Antrag konnte nicht eingereicht werden. Prüfen Sie die Felder und versuchen Sie es erneut',
      uploadFailed:
        'Die Angaben kamen durch, die Dateien nicht. Versuchen Sie, sie erneut anzuhängen.',
      uploadRetry: 'Dokumente hochladen',
      submitted: 'Antrag eingereicht',
      submittedHint: 'Ein Operator prüft die Dokumente und aktualisiert den Status.',

      review: 'Antrag in Prüfung',
      reviewHint: 'Die Angaben können während der laufenden Prüfung nicht geändert werden.',
      approved: 'Verifizierung abgeschlossen',
      approvedHint: 'Ein Operator hat die Dokumente bestätigt.',
      rejected: 'Antrag abgelehnt',
      rejectedHint: 'Korrigieren Sie die Angaben und reichen Sie den Antrag erneut ein.',
      resubmit: 'Neue Dokumente nötig',
      resubmitHint: 'Laden Sie ein gültiges Dokument hoch und reichen Sie den Antrag erneut ein.',
      rejectionReason: 'Grund',
      submittedAt: (date: string) => `Eingereicht ${date}`,
      attached: 'Angehängte Dokumente',
      attachedEmpty: 'Diesem Antrag ist keine Datei angehängt',
      startNew: 'Erneut einreichen',
      step: (current: number, total: number): string => `Schritt ${current} von ${total}`,

      gate: {

        title: 'Verifizieren Sie sich, um Ihre Mittel zu nutzen',

        body: 'Einzahlen können Sie sofort. Handel, Signale, Übertragungen und Auszahlungen öffnen sich nach Freigabe Ihrer Dokumente — meist innerhalb eines Tages.',

        pending: 'Ihre Dokumente werden geprüft — der Zugriff öffnet sich nach ihrer Freigabe, meist innerhalb eines Tages.',

        action: 'Identität verifizieren',

      },

      kinds: {
        passport_main: 'Hauptseite',
        passport_registration: 'Meldeseite',
        selfie_with_document: 'Selfie mit dem Dokument',
        proof_of_address: 'Adressnachweis',
      },

      errors: {
        required: 'Bitte ausfüllen',
        number: 'Nur Buchstaben, Ziffern, Leerzeichen und Bindestriche',
        name: 'Nur Buchstaben, Leerzeichen, Apostrophe und Bindestriche',
        country: 'Land wählen',
        date: 'Datum wählen',
        issueFuture: 'Das Ausstellungsdatum darf nicht in der Zukunft liegen',
        expiryPast: 'Das Dokument ist abgelaufen',
        expiryBeforeIssue: 'Das Ablaufdatum muss nach dem Ausstellungsdatum liegen',
        age: 'Die Verifizierung ist ab 18 möglich',
        birthAfterIssue: 'Das Geburtsdatum muss vor dem Ausstellungsdatum liegen',
        fileType: 'Nur JPG, PNG oder PDF',
        fileSize: (size: string) => `Die Datei ist größer als ${size} MB`,
        documentsRequired: 'Hängen Sie ein Foto oder einen Scan des Dokuments an',
        fileMissing: 'Fügen Sie diese Datei hinzu',
      },
    },
  },

  countries: {
    search: 'Ländername oder Code',
    popular: 'Beliebt',
    all: 'Alle Länder',
    nothing: 'Nichts gefunden. Versuchen Sie den englischen Namen oder einen Code wie DE',
    found: (count: number): string => `Gefundene Länder: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `Promo-Bonus von ${amount} USDT freigeschaltet und dem Spot-Konto gutgeschrieben`,
    promoExpired: 'Der Promo-Bonus ist verfallen: Die Bedingungen wurden nicht rechtzeitig erfüllt',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `Ihr Eingeladener ${name} hat sich qualifiziert: +${signals.text} Bonus-${pluralEn(signals, 'Signal', 'Signale')}`
        : `Ihr Eingeladener ${name} hat sich qualifiziert`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} Bonus-${pluralEn(signals, 'Signal', 'Signale')}` : null,
        promo === null ? null : `ein Promo-Bonus von ${promo} USDT, gesperrt bis zur Erfüllung der Bedingung`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'Ihre Empfehlungsqualifizierung ist abgeschlossen' : `Qualifizierung abgeschlossen: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'Qualifizierung widerrufen: Innerhalb der Haltefrist wurde eine Auszahlung beantragt. Die Bonus-Signale und der Promo-Bonus werden entzogen'
          : 'Qualifizierung widerrufen: Die Bonus-Signale und der Promo-Bonus werden entzogen'
        : `Die Qualifizierung Ihres Eingeladenen ${name} wurde widerrufen: Die dafür erhaltenen Bonus-Signale werden entzogen`,
    referralRewardPaid: (amount: string): string => `${amount} USDT im Rahmen des Partnerprogramms ausgezahlt`,
    referralRewardRejected: (amount: string): string => `Eine Prämie von ${amount} USDT wurde vom Operator abgelehnt`,
    partnerLevelReached: (level: string, amount: string): string =>
      `${level} erreicht: Ein Bonus von ${amount} USDT ist zur Auszahlung eingereiht`,
    title: 'Benachrichtigungen',
    settingsTitle: 'Benachrichtigungseinstellungen',
    settingsHint: 'Eine stummgeschaltete Kategorie bleibt in der Historie, verlangt aber keine Aufmerksamkeit: kein Badge, kein Push. Push funktioniert, wenn es im Profil an ist.',
    channelInApp: 'In der App',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'Was der Bot an Ihren Chat sendet. Eine hier ausgeschaltete Kategorie bleibt aus Telegram heraus, bleibt aber in der App.',
    telegramLinked: (name: string): string => (name === '' ? 'Telegram verbunden' : `Telegram verbunden: ${name}`),
    telegramLinkedHint: 'Waren Sie das nicht, tippen Sie, um es zu trennen.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram getrennt' : `Telegram getrennt: ${name}`),
    telegramUnlinkedHint: 'Dieser Chat war mit einem anderen Konto verbunden.',
    categories: { deposit: 'Einzahlungen', withdrawal: 'Auszahlungen', copy: 'Copy-Trading', trade: 'Trades und Positionen', signal: 'Signale', promo: 'Promo-Boni', referral: 'Partnerprogramm', kyc: 'Verifizierung', system: 'System' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'Alle als gelesen markieren',
    markAllFailed: 'Die Benachrichtigungen konnten nicht als gelesen markiert werden',
    loadFailed: 'Benachrichtigungen konnten nicht geladen werden',
    empty:
      'Noch keine Benachrichtigungen. Gutgeschriebene Einzahlungen, Antworten auf Auszahlungsanfragen, Signale und Trade-Ergebnisse erscheinen hier.',
    unread: 'Ungelesen',
    today: 'Heute',
    yesterday: 'Gestern',
    depositCredited: (amount: string, asset: string) => `${amount} ${asset} gutgeschrieben`,
    withdrawalApproved: 'Auszahlung freigegeben',
    withdrawalRejected: 'Auszahlung abgelehnt',
    withdrawalSent: 'Auszahlung on-chain gesendet',
    marginCall: 'Margin Call',
    positionLiquidated: 'Position liquidiert',
    copyOpened: (master: string) => `${master} hat eine Position eröffnet`,
    copyClosed: (master: string) => `${master} hat eine Position geschlossen`,
    copyStopLoss: 'Abo-Stop-Loss ausgelöst: Positionen geschlossen, Mittel auf das Spot-Konto zurückgebucht',
    copyMasterStopped: 'Trader gestoppt: Das Kopieren endete, Mittel auf das Spot-Konto zurückgebucht',
    copyReleased: (amount: string) => `Auf Spot zurückgebucht: ${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `Gewinnbeteiligung an den Trader: ${commission} USDT auf ${profit} USDT Gewinn` : `Gewinnbeteiligung an ${master}: ${commission} USDT auf ${profit} USDT Gewinn`,
    signalLive: (title: string, until: string) => `Signal „${title}“ ist offen bis ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `Position ${symbol} bei ${price} liquidiert`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: Take Profit bei ${price} erreicht`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: Stop Loss bei ${price} erreicht`,
    orderFilled: (symbol: string, quantity: string, price: string) => `Limit-Order ${symbol} ausgeführt: ${quantity} bei ${price}`,
    orderRejected: (symbol: string) => `Limit-Order ${symbol} nicht ausgeführt: Die Engine hat den Trade abgelehnt`,
    orderRejectedFunds: (symbol: string) => `Limit-Order ${symbol} nicht ausgeführt: Nicht genug Margin auf dem Futures-Konto`,
    orderRejectedLeverage: (symbol: string) => `Limit-Order ${symbol} nicht ausgeführt: Der Hebel übersteigt das Limit für diese Größe`,
    kycApproved: 'Verifizierung freigegeben',
    kycRejected: 'Verifizierung abgelehnt',
    kycResubmit: 'Verifizierung benötigt neue Dokumente',
    kycExpiring: (date: string) => `Ihr Dokument läuft am ${date} ab — erneuern Sie Ihre Verifizierung`,
    kycExpired: 'Ihr Dokument ist abgelaufen — laden Sie ein gültiges hoch',
    signalClosed: (title: string) => `Signal geschlossen: ${title}`,
    systemAlert: (check: string) => `Störung: ${check}`,
    systemRecovered: (check: string) => `Behoben: ${check}`,
    heads: {
      signalLive: 'Signal eröffnet',
      signalClosed: 'Signal geschlossen',
      orderFilled: 'Limit-Order ausgeführt',
      orderRejected: 'Limit-Order nicht ausgeführt',
      takeProfit: 'Take Profit erreicht',
      stopLoss: 'Stop Loss erreicht',
      promoUnlocked: 'Promo-Bonus freigeschaltet',
      promoExpired: 'Promo-Bonus verfallen',
      referralQualified: 'Eingeladener qualifiziert',
      referralWelcome: 'Qualifizierung abgeschlossen',
      referralRevoked: 'Qualifizierung widerrufen',
      rewardPaid: 'Prämie ausgezahlt',
      rewardRejected: 'Prämie abgelehnt',
      partnerLevelReached: 'Neue Partnerstufe',
      kycExpiring: 'Dokument läuft bald ab',
      kycExpired: 'Dokument abgelaufen',
      systemAlert: 'Störung',
      systemRecovered: 'Behoben',
    },
    fallback: {
      deposit: 'Einzahlung gutgeschrieben',
      withdrawal: 'Auszahlungsanfrage aktualisiert',
      trade: 'Positionsereignis',
      copy: 'Copy-Trading-Ereignis',
      referral: 'Partnerprogramm-Ereignis',
      system: 'Systembenachrichtigung',
    },
  },

  referrals: {
    title: 'Partnerprogramm',
    rewardKind: {
      fee_rebate: 'Gebührenanteil',
      signal_grant: 'Bonus-Signale',
      promotional_credit: 'Aktionsguthaben',
      deposit_bonus: 'Einzahlungsanteil',
      welcome_bonus: 'Willkommensanteil',
      level_bonus: 'Stufenbonus',
    },
    rewardStatus: {
      pending_review: 'In Prüfung',
      approved: 'Freigegeben',
      paid: 'Ausgezahlt',
      rejected: 'Abgelehnt',
      reversed: 'Zurückgebucht',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'Gesperrt',
      unlocked: 'Gutgeschrieben',
      expired: 'Verfallen',
      revoked: 'Widerrufen',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'Mitglied',
      bronze: 'Bronze',
      silver: 'Silber',
      gold: 'Gold',
      platinum: 'Platin',
      ambassador: 'Botschafter',
    } as Record<string, string>,
    signalUnit: 'Signale',
    membersFrom: (count: Count): string => `ab ${count.text} ${pluralEn(count, 'Mitglied', 'Mitgliedern')}`,
    baseLevel: 'Startstufe',
    depositFrom: (amount: string): string => `Einzahlung ab ${amount}`,
    noWeeklySignals: 'keine Signale',
    perWeek: (count: Count): string => `${count.text} ${pluralEn(count, 'Signal', 'Signale')}/Wo.`,
    current: 'aktuell',
    copyFailed: 'Konnte nicht kopiert werden',
    inviteText: 'Komm mit meinem Empfehlungscode zu 19CRT',
    linkCopied: 'Link kopiert',
    codeCopied: 'Code kopiert',
    loadError: 'Das Partnerprogramm konnte nicht geladen werden',
    loadErrorHint: 'Prüfen Sie Ihre Verbindung und versuchen Sie es erneut',
    retry: 'Erneut versuchen',
    disabled: 'Das Partnerprogramm ist vorübergehend pausiert. Ihre Statistiken und Prämien sind sicher.',
    yourCode: 'Ihr Code',
    codeHint: 'Laden Sie direkt ein — alle Bedingungen sind vor der Registrierung sichtbar',
    openQr: 'Empfehlungs-QR-Code öffnen',
    qrCode: 'QR-Code',
    share: 'Teilen',
    copy: 'Kopieren',
    yourLevel: 'Ihre Stufe',
    participant: 'Mitglied',
    allLevels: 'Alle Stufen',
    progressLabel: 'Fortschritt zur nächsten Stufe',
    qualified: (): string => 'qualifiziert',
    nextLevel: (remaining: Count, level: string): string => `noch ${remaining.text} bis ${level}`,
    maxLevel: 'höchste Stufe erreicht',
    invited: 'Eingeladen',
    confirmed: 'Qualifiziert',
    paidUsdt: 'Ausgezahlt, USDT',
    signalsReceived: 'Erhaltene Signale',
    transparentTitle: 'Transparente Prämien',
    transparentHint:
      'Prämien basieren nur auf tatsächlich eingenommenen Handelsgebühren. Die Einzahlung Ihres Eingeladenen wird nie umverteilt.',
    rewards: 'Prämien',
    rewardHistory: 'Prämienhistorie',
    underReview: 'In Prüfung',
    emptyRewardsTitle: 'Noch keine Prämien',
    emptyRewards: 'Prämien erscheinen nach der ersten qualifizierenden Aktivität Ihres Teams.',
    levelsRules: 'Stufen und Regeln',
    levelsRulesHint: 'Team, Gebührenanteil und Signale',
    inviteTitle: 'Zu 19CRT einladen',
    inviteSubtitle: 'Der Code wird bei der Registrierung verknüpft',
    copyLink: 'Link kopieren',
    levelsTitle: 'Partnerstufen',
    levelsSubtitle: 'Nur qualifizierte direkte Mitglieder zählen',
    loadLevelsError: 'Stufen konnten nicht geladen werden',
    levelTeam: 'Stufe und Team',
    feeSignals: 'Gebührenanteil · Signale',
    depositTiersTitle: 'Sätze nach Einzahlungsgröße',
    depositTiersHint: 'Je größer die erste Einzahlung, desto größer der Anteil für beide Seiten',
    tierDeposit: 'Einzahlung',
    tierReferrer: 'Sie',
    tierWelcome: 'Neuling',
    tierFrom: (amount: string): string => `${amount} und mehr`,
    levelBonus: 'Stufenbonus',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'Programmregeln',
      qualification: (amount: string, asset: string): string =>
        `Qualifizierung: eine erste gutgeschriebene Einzahlung von mindestens ${amount} ${asset} in einer Transaktion.`,
      qualificationKyc: 'Eine Identitätsverifizierung ist erforderlich.',
      welcome: (signals: Count, days: Count): string =>
        `Der Neuling erhält ${signals.text} Bonus-${pluralEn(signals, 'Signal', 'Signale')}, gültig für ${days.text} ${pluralEn(days, 'Tag', 'Tage')}.`,
      referrer: (signals: Count, days: Count): string =>
        `Der Partner erhält für jeden qualifizierten Eingeladenen ${signals.text} Bonus-${pluralEn(signals, 'Signal', 'Signale')}, gültig für ${days.text} ${pluralEn(days, 'Tag', 'Tage')}.`,
      promo: (amount: string, required: string, days: Count): string =>
        `Ein Promo-Bonus von ${amount} USDT wird gesperrt und wird zu Spot-Guthaben, sobald der gehandelte Umsatz des Neulings innerhalb von ${days.text} ${pluralEn(days, 'Tag', 'Tagen')} ${required} USDT erreicht; andernfalls verfällt er.`,
      hold: (days: Count): string =>
        `Jede Auszahlungsanfrage innerhalb von ${days.text} ${pluralEn(days, 'Tag', 'Tagen')} nach der Qualifizierung hebt sie zusammen mit den Bonus-Signalen und dem Promo-Bonus auf.`,
      feeShare:
        'Der Gebührenanteil der Stufe wird nur aus tatsächlich vom Eingeladenen eingenommenen Handelsgebühren berechnet. Geldprämien werden vom Operator geprüft und separat ausgezahlt — sie werden nie dem Guthaben gutgeschrieben.',
      secondLevel: (rate: string): string => `Der Partner des Partners erhält ${rate} derselben Gebühr.`,
      depositTiers:
        'Der Anteil für den Einladenden und für den Neuling hängt von der Größe der ersten Einzahlung ab — siehe die Tabelle in diesem Blatt.',
      balanceShare: (percent: string): string =>
        `Die Einzahlung muss bei der Gutschrift außerdem mindestens ${percent}% des Guthabens des Einladenden betragen.`,
      topup: (days: Count): string =>
        `Eine weitere Einzahlung innerhalb von ${days.text} ${pluralEn(days, 'Tag', 'Tagen')} nach der ersten wird ihr hinzugerechnet, das Paar wird anhand der Tabelle neu bewertet und die Differenz einmalig ausgezahlt.`,
      premiumSignal: (count: string): string =>
        `Dauerhafter Zugang zu Premium-Signalen öffnet sich, sobald Sie ${count} oder mehr qualifizierte Eingeladene haben.`,
      antiabuse:
        'Ein mit dem Partner oder seinem Team geteiltes Gerät oder eine geteilte Adresse schickt die Qualifizierung zur Prüfung an den Operator.',
      paused: 'Das Programm ist vom Operator pausiert: Es werden keine neuen Qualifizierungen oder Prämien erstellt.',
    },
    disclaimer:
      'Partnerprämien sind kein garantiertes Einkommen. Registrierung, Einzahlungen und Handelsvolumen erzeugen für sich genommen keine Geldauszahlung.',
    team: 'Team',
    teamStatus: {
      pending: 'wartet auf eine Einzahlung',
      flagged: 'in Prüfung durch den Operator',
      qualified: 'qualifiziert',
      revoked: 'Qualifizierung widerrufen',
    },
    joined: (date: string): string => `seit ${date}`,
    showAll: (count: Count): string => `Alle anzeigen (${count.text})`,
    showLess: 'Weniger anzeigen',
    promoTitle: 'Promo-Bonus',
    promoLocked: (amount: string): string => `${amount} USDT gesperrt`,
    promoUnlocked: (amount: string): string => `${amount} USDT dem Spot-Konto gutgeschrieben`,
    promoExpired: 'Verfallen, der Bonus ist weg',
    promoRevoked: 'Bonus widerrufen',
    promoCondition: (traded: string, required: string): string => `Umsatz ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `bis ${date}`,
    promoHint: 'Der Bonus wird zu Geld, sobald Ihr gehandelter Umsatz die Bedingung vor der Frist erreicht.',
  },

  signals: {
    title: 'Signale',
    tabs: { today: 'Heute', history: 'Historie' },
    tabsLabel: 'Signalbereiche',
    access: { public: 'Öffentlich', base: 'Basis', premium: 'Premium', bonus: 'Bonus', agent: 'Partner' },
    status: { scheduled: 'Geplant', available: 'Verfügbar', executed: 'Ausgeführt', expired: 'Abgelaufen', cancelled: 'Storniert' },
    side: { buy: 'Kaufen', sell: 'Verkaufen' },
    direction: { buy: 'Long · Kauf', sell: 'Short · Verkauf' },
    positionSide: { buy: 'Long', sell: 'Short' },
    localTime: 'Ortszeit',
    localClock: 'Ortszeit',
    timeZone: (offset: string) => `Zeitzone ${offset}`,
    reason: {
      unavailable: 'Dieses Signal ist gerade nicht verfügbar',
      already: 'Sie haben dieses Signal bereits ausgeführt',
      expired: 'Das Ausführungsfenster ist abgelaufen',
      notReleased: 'Dieses Signal hat noch nicht geöffnet',
      cancelled: 'Dieses Signal wurde storniert',
      balance: 'Nicht genug Guthaben auf Ihrem Spot-Konto',
      balanceFutures: 'Nicht genug USDT auf Spot- und Futures-Konto zusammen',
      positionConflict: 'Sie haben in diesem Markt eine offene Position in die entgegengesetzte Richtung — schließen Sie sie, um das Signal auszuführen',
      price: 'Der Kurs für diesen Markt ist gerade nicht verfügbar — versuchen Sie es in ein paar Sekunden erneut',
      access: 'Für dieses Signal ist eine andere Zugriffsstufe erforderlich',
      kyc: 'Schließen Sie zuerst die Verifizierung ab — nach Freigabe Ihrer Dokumente können Sie das Signal ausführen',
      minimum: 'Dieses Signal nimmt einen Anteil Ihres Gesamtguthabens, und dieser Anteil liegt unter dem Minimum. Laden Sie Ihr Guthaben auf, um es auszuführen',
      maximum: 'Der Betrag liegt über dem Maximum dieses Signals',
      belowMinimum: (amount: string) => `Dieses Signal nimmt einen Anteil Ihres Gesamtguthabens, und dieser Anteil liegt unter seinem Minimum von ${amount} USDT. Laden Sie Ihr Guthaben auf, um es auszuführen`,
      aboveMaximum: (amount: string) => `Der Betrag liegt über dem Maximum dieses Signals — höchstens ${amount} USDT`,
      notFound: 'Für diesen Code wurde kein Signal gefunden',
      generic: 'Die Aktion konnte nicht abgeschlossen werden. Prüfen Sie die Bedingungen und versuchen Sie es erneut',
      network: 'Der Server war nicht erreichbar. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut',
    },
    enterCode: 'Code eingeben',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'Geben Sie den Code aus dem Kanal ein, um Richtung, Größe und Bedingungen vor der Bestätigung zu sehen', executed: 'Das Ergebnis und die tatsächlichen Parameter stehen in der Historie', scheduled: (time: string) => `Der Code erscheint im Kanal um ${time}` },
    executing: 'Wird ausgeführt',
    notExecuted: 'Nicht ausgeführt',
    executedLabel: 'Ausgeführt',
    loadSignalsError: 'Signale konnten nicht geladen werden',
    noSignalsToday: 'Heute keine Signale',
    loadErrorHint: 'Prüfen Sie Ihre Verbindung und versuchen Sie es erneut',
    retry: 'Erneut versuchen',
    signalAvailable: 'Signal verfügbar',
    nextSignal: 'Nächstes Signal',
    windowOpenUntil: (time: string) => `Fenster offen bis ${time}`,
    opensAt: (time: string) => `Öffnet um ${time}`,
    execute: 'Ausführen',
    doneToday: 'Für heute alles erledigt',
    newBySchedule: 'Neue Signale erscheinen nach Zeitplan',
    codeOverline: 'Signalcode',
    checkTerms: 'Bedingungen prüfen',
    codeHint: 'Jeder Code wird in unserem Telegram-Kanal veröffentlicht — geben Sie ihn ein, sobald Sie ihn sehen.',
    openChannel: 'Kanal öffnen',
    channelLink: 'Telegram-Kanal',
    panelTitle: 'Code handeln',
    dealsTitle: 'Trades per Code',
    dealsEmpty: 'Noch keine Trades per Code',
    dealsEmptyHint: 'Geben Sie einen Code aus dem Kanal ein — der Trade und sein Ergebnis erscheinen hier',
    codePlaceholder: 'Zum Beispiel 1QF9ET7RQ',
    codeRequired: 'Geben Sie den Code aus dem Kanal ein',
    codeLabel: 'Signalcode',
    check: 'Prüfen',
    schedule: 'Zeitplan',
    todaySignals: 'Heutige Signale',
    noSignals: 'Gerade keine Signale. Neue Codes erscheinen nach Zeitplan in unserem Telegram-Kanal.',
    riskDisclosure:
      'Ein Signal ist eine Handelsidee, kein garantiertes Ergebnis. Vor der Bestätigung sehen Sie Richtung, Betrag, Hebel, Gebühr und Ausstiegsbedingungen.',
    loadHistoryError: 'Die Signalhistorie konnte nicht geladen werden',
    historyEmpty: 'Noch keine Historie',
    historyEmptyHint: 'Ausgeführte Signale und ihre tatsächlichen Parameter erscheinen hier.',
    toSignals: 'Signale ansehen',
    allExecutions: 'Alle Ausführungen',
    signalAccepted: 'Signal angenommen',
    confirmation: 'Signal bestätigen',
    sentToTrading: 'Die Order wurde an das Handelssystem gesendet',
    checkBeforeExecute: 'Prüfen Sie die Bedingungen vor der Ausführung',
    acceptedHint: 'Erneutes Tippen erstellt keine zweite Order.',
    ready: 'Fertig',
    account: 'Konto',
    spot: 'Spot',
    futures: 'Futures',
    perpetualMarket: 'Futures',
    validUntil: 'Gültigkeit',
    signalName: 'Signal',
    until: (time: string) => `bis ${time}`,
    timeLeft: (countdown: string) => `noch ${countdown}`,
    priceGuide: 'Richtkurs',
    entryGuide: 'Richt-Einstieg',
    marketPrice: 'Markt',
    stopLoss: 'Stop Loss',
    takeProfit: 'Take Profit',
    autoClose: 'Auto-Schließung',
    inDuration: (duration: string) => `in ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days} T` : null, hours > 0 ? `${hours} Std.` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes} Min.` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'Bereits ausgeführt',
    executionUnavailable: 'Ausführung nicht verfügbar',
    executionAmount: 'Ausführungsbetrag',
    marginAmount: 'Margin',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} Ihres Gesamtguthabens · ${total} USDT`,
    estimatedFee: 'Geschätzte Gebühr',
    totalDebit: 'Gesamtbelastung',
    positionSize: 'Positionsgröße',
    willSell: 'Zu verkaufen',
    fundingNote: (amount: string) =>
      `${amount} USDT werden für diesen Trade von Ihrem Spot-Konto auf Ihr Futures-Konto verschoben. Beim Schließen der Position kehrt dieser Betrag, um das Ergebnis angepasst, auf Ihr Spot-Konto zurück.`,
    executionRisk:
      'Ein Signal ist eine Handelsidee, kein garantiertes Ergebnis. Der Ausführungskurs kann vom Richtwert abweichen, und der Trade kann Verluste bringen. Bestätigen Sie nur ein Risiko, das Sie verstehen.',
    executionRiskFutures:
      'Ein Signal ist eine Handelsidee, kein garantiertes Ergebnis. Der Einstiegskurs kann vom Richtwert abweichen, und der Hebel verstärkt Gewinn wie Verlust, bis zur Liquidation der Position. Bestätigen Sie nur ein Risiko, das Sie verstehen.',
    retryExecution: 'Ausführung wiederholen',
    outcomeLabel: 'Ergebnis, USDT',
    outcomeOpen: 'offen',
    outcome: { take_profit: 'am Ziel geschlossen', stop_loss: 'am Stop geschlossen', time: 'bei Ablauf geschlossen', manual: 'manuell geschlossen', liquidation: 'durch Liquidation geschlossen' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'Am Ziel', stop_loss: 'Am Stop', time: 'Bei Ablauf', manual: 'Manuell', liquidation: 'Liquidiert' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'Offen',
    executeFor: 'Ausführen bis zu',
    openLong: (amount: string) => `Long eröffnen · ${amount} USDT`,
    openShort: (amount: string) => `Short eröffnen · ${amount} USDT`,
    submittedToast: 'Signal zur Ausführung eingereicht',
    filledOf: (requested: string, filled: string) =>
      `Angefragt ${requested} USDT, ausgeführt ${filled} USDT — die Menge wurde auf die Lot-Schrittweite gerundet.`,
    marginRounded: (requested: string) => `Angefragt ${requested} USDT — die Menge wurde auf die Lot-Schrittweite gerundet.`,
    longOpened: (margin: string, leverage: number) => `Long eröffnet: ${margin} USDT Margin bei ${leverage}× Hebel.`,
    shortOpened: (margin: string, leverage: number) => `Short eröffnet: ${margin} USDT Margin bei ${leverage}× Hebel.`,
    fundedFromSpot: (amount: string) => `${amount} USDT von Ihrem Spot-Konto verschoben.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'Take Profit' : null, stopLoss ? 'Stop Loss' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `Schließt automatisch in ${duration}`
      const byLevel = `bei ${levels.join(duration === null ? ' oder ' : ', ')}`
      return duration === null ? `Schließt ${byLevel}` : `Schließt ${byLevel} oder in ${duration}`
    },
    openPosition: 'Zur Position',
    returnedToSpot: (amount: string) => `Auf Spot zurückgebucht: ${amount} USDT`,
    positionAccepted: 'Die Position ist auf Ihrem Futures-Konto offen',
  },

  notFound: {
    title: 'Seite nicht gefunden',
    message: 'Hier gibt es keine solche Seite — der Link ist veraltet oder die Adresse hat einen Tippfehler.',
    action: 'Zur Startseite',
  },
}
